import { IResetPasswordWidgetData, IResetPasswordWidgetShortData, TranslateService } from "@pageBuilder/typings";
import { ResetPasswordScriptRenderer } from "./reset-password-script-renderer";
import { IRenderingCommonContext } from "@commonRendering/types";
import { DomUtil } from "src/utils/dom-util";
import { MembersAreaWidgetRendererBase } from "../common/members-area-widget-renderer-base";

export class ResetPasswordWidgetRenderer extends MembersAreaWidgetRendererBase<IResetPasswordWidgetData> {

    protected readonly DATA_ATTRIBUTE = "data-reset-password";
    private readonly scriptRenderer: ResetPasswordScriptRenderer;
    private readonly translate: any;

    constructor(renderingCommonContext: IRenderingCommonContext) {
        super(renderingCommonContext);
        this.scriptRenderer = new ResetPasswordScriptRenderer();
        this.translate = TranslateService.translator(renderingCommonContext.data.lang);
    }

    protected createNewInternal(): HTMLElement {
        return this.createWidgetElement("resetPasswordWidget");
    }

    protected override renderInternal(element: HTMLElement, data: IResetPasswordWidgetData): HTMLElement {
        this.setAttributes(element, data);
        this.renderContent(element, data);

        this.scriptRenderer.renderCommon(this.renderedDocument);
        this.scriptRenderer.renderLocal(element);

        this.renderStyles(element, data);

        return element;
    }

    private renderContent(element: HTMLElement, data: IResetPasswordWidgetData): void {
        const content = element.querySelector<HTMLElement>(".widget-content");
        if (!content) {
            return;
        }

        DomUtil.deleteAllChildElements(content);

        this.renderTitle(content);
        this.renderSubtitle(content);
        this.renderResetPasswordForm(content, data);
    }

    private renderTitle(container: HTMLElement): void {
        const title = document.createElement("span");
        title.classList.add("title");
        title.textContent = this.translate.RESET_PASSWORD.DEFAULT_TITLE;
        title.title = title.textContent ?? "";

        container.appendChild(title);
    }

    private renderSubtitle(container: HTMLElement): void {
        const subtitle = document.createElement("span");
        subtitle.classList.add("subtitle");
        subtitle.textContent = this.translate.RESET_PASSWORD.DEFAULT_SUBTITLE;
        subtitle.title = subtitle.textContent ?? "";

        container.appendChild(subtitle);
    }

    private renderResetPasswordForm(container: HTMLElement, data: IResetPasswordWidgetData): void {
        const form = this.ensureFormElement(container);
        DomUtil.deleteAllChildElements(form);

        this.renderControls(form, data);
        this.renderSubmitButton(form, data.style.submit?.theme ?? "primary", data.submit.text);
    }

    private renderControls(form: HTMLElement, data: IResetPasswordWidgetData): void {
        const newPasswordFieldData = data.fields?.find(x => x.type === "Password");
        const confirmPasswordFieldData = data.fields?.find((x, i) => i !== 0 && x.type === "Password");

        if (!newPasswordFieldData || !confirmPasswordFieldData) {
            return;
        }

        const labelsStyle = data.style.labels?.style ?? "standard";

        this.renderControl(form, newPasswordFieldData, {
            inputName: "register-email",
            inputType: newPasswordFieldData.type,
            inputMaxLength: this.PASSWORD_MAX_LENGTH,
            autocomplete: this.isEditorRendering ? "new-password" : "current-password",
            labelText: newPasswordFieldData.caption,
            labelStyle: labelsStyle
        });

        this.renderControl(form, confirmPasswordFieldData, {
            inputName: "register-password",
            inputType: confirmPasswordFieldData.type,
            inputMaxLength: this.PASSWORD_MAX_LENGTH,
            autocomplete: this.isEditorRendering ? "new-password" : "current-password",
            labelText: confirmPasswordFieldData.caption,
            labelStyle: labelsStyle
        });
    }

    protected override prepareDataForSave(data: IResetPasswordWidgetData): IResetPasswordWidgetShortData {
        return {
            fields: data.fields,
            submit: data.submit
        } as IResetPasswordWidgetShortData;
    }

    protected override getContentSelectorsInternal(element: HTMLElement): Array<string> {
        return [ `#${element.id}` ];
    }

}
